import { useContext } from "react";
import { Accordion, Badge, Card, Table } from "react-bootstrap";
import { useAuthenticatedFetch } from "../../../lib/api";
import { rebalanceCheckPath } from "../../lib";
import type { UnpackResponse } from "../../../../../api/src/lib";
import type { RebalancesController } from "../../../../../api/src/rebalances/rebalances.controller";
import type {
  AccountRecord,
  CheckpointKey,
  DivergenceAssessment,
  FindingCategory,
  FindingSeverity,
  FindingSource,
} from "../lib/explainTypes";
import { RebalanceContext } from "../../RebalanceInfo";
import { useAccountResolver } from "../hooks/useAccountResolver";

type CheckData = UnpackResponse<RebalancesController["getRebalanceCheck"]>;

// The shared findings interface, extended to the D7 audit contract
// (s-rebalance-audit-analysis-pipeline-019eb94b D7). The existing
// `code/severity/title/description/recommendation?/agent?/account_id?/symbol?`
// fields are unchanged; `checkpoint`/`source`/`category`/`asset_class?` are the
// D7 additions. They are OPTIONAL here because `/v1/rebalances/:id/check` findings
// (the legacy check engine) predate the audit contract and do not carry them;
// the audit artifact's findings (typed `AuditFinding` in `explainTypes`) carry
// them as required. A `spec_divergence` finding also carries `divergence_found`.
interface Finding {
  code: string;
  severity: FindingSeverity;
  title: string;
  description: string;
  recommendation?: string;
  agent?: string;
  account_id?: number;
  symbol?: string;
  checkpoint?: CheckpointKey;
  source?: FindingSource;
  category?: FindingCategory;
  asset_class?: string;
  divergence_found?: DivergenceAssessment;
}

interface Props {
  rebalanceId: number;
  accounts: AccountRecord[];
}

const statusVariant = (
  status: string,
): "success" | "warning" | "danger" | "secondary" => {
  switch (status) {
    case "pass":
      return "success";
    case "review_recommended":
      return "warning";
    case "action_required":
      return "danger";
    default:
      return "secondary";
  }
};

const severityVariant = (
  severity: string,
): "danger" | "warning" | "info" | "secondary" => {
  switch (severity) {
    case "critical":
    case "error":
      return "danger";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "secondary";
  }
};

const statusLabel = (status: string): string => {
  switch (status) {
    case "pass":
      return "Pass";
    case "review_recommended":
      return "Review Recommended";
    case "action_required":
      return "Action Required";
    default:
      return status;
  }
};

const SEVERITY_ORDER = ["critical", "error", "warning", "info"] as const;

// v3 port of the legacy `RebalanceCheckPanel`. Wraps the same
// `/v1/rebalances/:id/check` data in a Card-shaped panel consistent with
// the rest of the v3 explain tree, and resolves account_id -> name
// via the same resolver used elsewhere on the page.
export default function ChecksPanel({ rebalanceId, accounts }: Props) {
  // A caller that gives the check (the admin Explain viewer, R23) gets no
  // `/check` read; every other caller reads it as before.
  const { givenCheck } = useContext(RebalanceContext);
  const { data: fetched, isPending: fetchPending } =
    useAuthenticatedFetch<CheckData>(
      rebalanceCheckPath(rebalanceId),
      {},
      { enabled: !!rebalanceId && !givenCheck },
    );
  const data = givenCheck ? { data: givenCheck.check } : fetched;
  const isPending = givenCheck ? false : fetchPending;

  const resolve = useAccountResolver();
  const accountById = new Map(accounts.map((a) => [a.account_id, a]));

  if (isPending) return null;
  const check = data?.data;
  if (!check) return null;

  const findings: Finding[] = Array.isArray(check.findings)
    ? (check.findings as unknown as Finding[])
    : [];
  const recommendations: string[] = Array.isArray(check.recommendations)
    ? check.recommendations
    : [];

  const groupedFindings: Record<string, Finding[]> = {};
  for (const f of findings) {
    const sev = f.severity || "info";
    (groupedFindings[sev] ||= []).push(f);
  }

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
        <span>Checks</span>
        <Badge bg={statusVariant(check.overallStatus)} className="ms-2">
          {check.overallScore}/100
        </Badge>
        <Badge bg={statusVariant(check.overallStatus)}>
          {statusLabel(check.overallStatus)}
        </Badge>
        <span className="ms-auto d-flex gap-1">
          {check.findingCounts?.critical > 0 && (
            <Badge bg="danger">{check.findingCounts.critical} critical</Badge>
          )}
          {check.findingCounts?.error > 0 && (
            <Badge bg="danger">
              {check.findingCounts.error} error
              {check.findingCounts.error !== 1 && "s"}
            </Badge>
          )}
          {check.findingCounts?.warning > 0 && (
            <Badge bg="warning">
              {check.findingCounts.warning} warning
              {check.findingCounts.warning !== 1 && "s"}
            </Badge>
          )}
          {check.findingCounts?.info > 0 && (
            <Badge bg="info">{check.findingCounts.info} info</Badge>
          )}
        </span>
      </Card.Header>
      <Card.Body>
        {check.summary && <p className="mb-2">{check.summary}</p>}

        {findings.length > 0 && (
          <Accordion className="mb-2">
            {SEVERITY_ORDER.filter((sev) => groupedFindings[sev]?.length).map(
              (sev) => (
                <Accordion.Item eventKey={sev} key={sev}>
                  <Accordion.Header>
                    <Badge bg={severityVariant(sev)} className="me-2">
                      {sev.toUpperCase()}
                    </Badge>
                    {groupedFindings[sev].length} finding
                    {groupedFindings[sev].length !== 1 && "s"}
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table size="sm" bordered>
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Title</th>
                          <th>Description</th>
                          <th>Recommendation</th>
                          <th>Context</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedFindings[sev].map((f, idx) => {
                          const acct =
                            f.account_id != null
                              ? accountById.get(f.account_id)
                              : undefined;
                          const r = acct ? resolve(acct) : null;
                          return (
                            <tr
                              key={`${f.code}-${idx}`}
                              className={
                                sev === "critical" ? "fw-bold" : undefined
                              }
                            >
                              <td>
                                <code>{f.code}</code>
                              </td>
                              <td>{f.title}</td>
                              <td>{f.description}</td>
                              <td>{f.recommendation || "—"}</td>
                              <td>
                                {f.agent && (
                                  <small className="d-block text-muted">
                                    {f.agent}
                                  </small>
                                )}
                                {f.account_id != null && (
                                  <small className="d-block">
                                    {r?.name ?? `Account ${f.account_id}`}
                                    {r?.number && (
                                      <span className="text-muted">
                                        {" "}
                                        ({r.number})
                                      </span>
                                    )}
                                  </small>
                                )}
                                {f.symbol && (
                                  <small className="d-block">{f.symbol}</small>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              ),
            )}
          </Accordion>
        )}

        {recommendations.length > 0 && (
          <div>
            <strong>Recommendations</strong>
            <ul className="mb-0">
              {recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
